import React, { useEffect, useRef, useState } from 'react';

interface ReCaptchaProps {
    siteKey: string;
    version: 'checkbox' | 'score';
    onTokenChange: (token: string) => void;
    className?: string;
}

declare global {
    interface Window {
        grecaptcha: { enterprise: any };
    }
}

const RECAPTCHA_REFRESH_INTERVAL = 60000;

/**
 * ReCaptcha component that supports both score-based and checkbox Enterprise keys.
 * Checkbox: Shows a visible widget
 * Score: Invisible, automatically generates token
 */
export default function ReCaptcha({
    siteKey,
    version,
    onTokenChange,
    className = ''
}: ReCaptchaProps) {
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<number | null>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    /**
     * Loads the reCAPTCHA Enterprise script dynamically
     */
    const loadRecaptchaScript = (): Promise<void> => {
        return new Promise((resolve) => {
            if (window.grecaptcha?.enterprise) {
                setIsLoaded(true);
                resolve();
                return;
            }

            const existingScript = document.querySelector('script[src*="recaptcha/enterprise.js"]');
            if (existingScript) {
                existingScript.addEventListener('load', () => {
                    setIsLoaded(true);
                    resolve();
                });
                return;
            }

            const script = document.createElement('script');
            script.async = true;
            script.defer = true;

            if (version === 'score') {
                script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
            } else {
                script.src = 'https://www.google.com/recaptcha/enterprise.js';
            }

            script.addEventListener('load', () => {
                setIsLoaded(true);
                resolve();
            });

            document.body.appendChild(script);
        });
    };

    /**
     * Executes reCAPTCHA Enterprise score-based token generation
     */
    const executeScoreRecaptcha = () => {
        if (!window.grecaptcha?.enterprise?.ready) {
            return;
        }

        window.grecaptcha.enterprise.ready(() => {
            window.grecaptcha.enterprise.execute(siteKey, { action: 'submit' }).then((token: string) => {
                onTokenChange(token);
            });
        });
    };

    /**
     * Renders reCAPTCHA Enterprise checkbox widget
     */
    const renderCheckboxWidget = () => {
        if (!window.grecaptcha?.enterprise?.render || !containerRef.current) {
            return;
        }

        if (widgetIdRef.current !== null) {
            return;
        }

        widgetIdRef.current = window.grecaptcha.enterprise.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token: string) => {
                onTokenChange(token);
            },
            'expired-callback': () => {
                onTokenChange('');
            }
        });
    };

    useEffect(() => {
        loadRecaptchaScript();

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!isLoaded) {
            return;
        }

        if (version === 'score') {
            executeScoreRecaptcha();

            intervalRef.current = setInterval(() => {
                executeScoreRecaptcha();
            }, RECAPTCHA_REFRESH_INTERVAL);
        } else {
            if (window.grecaptcha?.enterprise?.ready) {
                window.grecaptcha.enterprise.ready(() => {
                    renderCheckboxWidget();
                });
            }
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isLoaded, version, siteKey]);

    if (version === 'score') {
        return null;
    }

    return (
        <div className={className}>
            <div ref={containerRef}></div>
        </div>
    );
}
